import React, { useState, useEffect } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇦🇪' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
];

function triggerGoogleTranslate(langCode) {
  // Clear existing cookies first to avoid conflicts
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname + ';';
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.' + window.location.hostname + ';';

  if (langCode !== 'en') {
    // Set new cookie for the selected language
    document.cookie = `googtrans=/en/${langCode}; path=/;`;
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=${window.location.hostname};`;
    document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${window.location.hostname};`;
  }
  
  // Reload to apply the translation
  window.location.reload();
}

function getCurrentLang() {
  const match = document.cookie.match(/googtrans=\/en\/(\w+)/);
  return match ? match[1] : 'en';
}

const LanguageSwitcher = ({ isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');
  useEffect(() => {
    setCurrentLang(getCurrentLang());
  }, []);

  const handleSelect = (code) => {
    setCurrentLang(code);
    setIsOpen(false);
    triggerGoogleTranslate(code);
  };

  const current = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  if (isMobile) {
    return (
      <div className="flex items-center gap-2 py-3 px-1">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
              currentLang === lang.code
                ? 'bg-accent/10 text-accent'
                : 'text-white/60 hover:bg-accent/10 hover:text-accent'
            }`}
          >
            {`${lang.flag} ${lang.code.toUpperCase()}`}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div 
      className="relative" 
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Button - no border, no background, matches nav link style */}
      <button
        className="flex items-center gap-1 px-3 py-2 text-lg font-semibold text-white hover:text-accent transition-colors cursor-pointer relative group"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
      >
        <span>{`${current.flag} ${current.code.toUpperCase()}`}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <HiChevronDown className="h-4 w-4" />
        </motion.span>
        {/* Same underline animation as other nav links */}
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transform transition-transform origin-center" />
      </button>

      {/* Dropdown - identical to services submenu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-48 rounded-2xl bg-primary/95 py-4 shadow-2xl ring-1 ring-black ring-opacity-10 border border-accent/20 overflow-hidden"
          >
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`block w-full text-left px-8 py-4 text-xl transition-colors ${
                  currentLang === lang.code
                    ? 'text-accent bg-accent/10'
                    : 'text-white hover:bg-accent/10 hover:text-accent'
                }`}
              >
                {`${lang.flag} ${lang.label}`}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
